import { getState, mutate, nextId, chainDigest, verifyChain, isPersistent, exportRecord } from "./store";
import { STATUS, transitionError } from "./incidentMachine";

/* ──────────────────────────────────────────────────────────────────────────
 * Repository — the operational record, browser-side.
 *
 * Frontend-only by decision, so every write goes to localStorage. The API is
 * async on purpose: the calling components already await it, and if a server
 * is ever added the implementation changes here and nowhere else.
 *
 * Every result carries `persisted` and `durable`:
 *   persisted — did the write reach localStorage (false in private mode)
 *   durable   — is this an auditable record. ALWAYS FALSE in the browser.
 *
 * That second flag exists so no surface can accidentally present browser
 * state as evidence. It is not pessimism: a record anyone can edit from
 * devtools, that no other officer can see, and that vanishes when site data
 * is cleared, is a demonstration artefact. Calling it an audit trail in
 * front of a government reviewer would be the single worst thing this
 * product could do.
 * ────────────────────────────────────────────────────────────────────────── */

const ENGINE_VERSION = "kumbh-rules-1.0.0";
const nowIso = () => new Date().toISOString();

/* Browser state is never durable. Stated once, here. */
const result = (data, persisted) => ({ data, persisted, durable: false });

export function storeHealth() {
  const s = getState();
  return {
    persistent: isPersistent(),
    chain: verifyChain(s.events),
    counts: {
      incidents: Object.keys(s.incidents).length,
      events: s.events.length,
      decisions: s.decisions.length,
      approvals: s.approvals.length,
      tasks: s.tasks.length,
      observations: s.observations.length,
    },
  };
}

export { exportRecord };

/* ── Incidents ────────────────────────────────────────────────────────── */
export async function createIncident(incident) {
  const { result: row, persisted } = mutate((s) => {
    if (s.incidents[incident.id]) return s.incidents[incident.id];

    const rec = {
      ...incident,
      status: STATUS.DETECTED,
      detected_at: incident.detected_at || nowIso(),
      updated_at: nowIso(),
    };
    s.incidents[rec.id] = rec;

    const at = nowIso();
    s.events.push({
      id: nextId(s),
      incident_id: rec.id,
      at,
      kind: "state_change",
      from_status: null,
      to_status: STATUS.DETECTED,
      payload: {},
      prev_digest: null,
      digest: chainDigest(null, {
        incident_id: rec.id, kind: "state_change", to_status: STATUS.DETECTED, at,
      }),
    });
    return rec;
  });
  return result(row, persisted);
}

export async function listIncidents() {
  return result(Object.values(getState().incidents), true);
}

export async function incidentTimeline(incidentId) {
  return result(getState().events.filter((e) => e.incident_id === incidentId), true);
}

export async function advanceIncident(incident, to, { actor, hasApproval, note } = {}) {
  /* Validated BEFORE the write. The log is supposed to record what
   * legitimately happened, so an illegal transition must never reach it. */
  const err = transitionError(incident.status, to, { hasApproval });
  if (err) throw new Error(err);

  const { result: row, persisted } = mutate((s) => {
    const inc = s.incidents[incident.id];
    if (!inc) throw new Error(`Incident ${incident.id} is not in the store.`);

    const at = nowIso();
    const prev = [...s.events].reverse().find((e) => e.incident_id === incident.id);
    const event = {
      id: nextId(s),
      incident_id: incident.id,
      at,
      kind: "state_change",
      from_status: inc.status,
      to_status: to,
      actor_id: actor?.id ?? null,
      actor_agency: actor?.agency ?? null,
      payload: note ? { note } : {},
      prev_digest: prev?.digest ?? null,
      digest: chainDigest(prev?.digest ?? null, {
        incident_id: incident.id, kind: "state_change", to_status: to, at,
      }),
    };
    s.events.push(event);

    inc.status = to;
    inc.updated_at = at;
    if (to === STATUS.ACKNOWLEDGED && !inc.acknowledged_at) inc.acknowledged_at = at;
    if (to === STATUS.RESOLVED && !inc.resolved_at) inc.resolved_at = at;
    return event;
  });
  return result(row, persisted);
}

/* ── Decisions ────────────────────────────────────────────────────────── */
export async function recordDecision(decision, incidentId = null) {
  const { result: row, persisted } = mutate((s) => {
    const rec = {
      id: `dec-${nextId(s)}`,
      incident_id: incidentId,
      sector_id: decision.focusSectorId ?? null,
      kind: decision.kind,
      priority: decision.priority,
      title_en: decision.title?.en ?? "",
      title_mr: decision.title?.mr ?? null,
      rationale_en: decision.rationale?.en ?? "",
      rationale_mr: decision.rationale?.mr ?? null,
      evidence: decision.evidence ?? [],
      confidence: decision.confidence ?? null,
      trust: decision.trust ?? "SIMULATED",
      lead_minutes: decision.leadMinutes ?? null,
      engine_version: ENGINE_VERSION,
      requires_approval: Boolean(decision.requiresApproval),
      offered_at: nowIso(),
    };
    s.decisions.push(rec);
    return rec;
  });
  return result(row, persisted);
}

/* ── Approvals — append only; a reversal is a new row ─────────────────── */
export async function recordApproval({ decisionId, officer, action, modification, reason }) {
  if (action === "modify" && !modification) {
    throw new Error("A modification must state what changed.");
  }
  const { result: row, persisted } = mutate((s) => {
    const rec = {
      id: `app-${nextId(s)}`,
      decision_id: decisionId,
      officer_id: officer?.id ?? null,
      officer_position: officer?.position ?? null,
      action,
      modification: modification ?? null,
      reason: reason ?? null,
      at: nowIso(),
    };
    s.approvals.push(rec);
    return rec;
  });
  return result(row, persisted);
}

export async function approvalsFor(decisionId) {
  return result(getState().approvals.filter((a) => a.decision_id === decisionId), true);
}

/* ── Tasks ────────────────────────────────────────────────────────────── */
export async function dispatchTasks(incidentId, tasks, decisionId = null) {
  const { result: rows, persisted } = mutate((s) => {
    const created = tasks.map((t) => ({
      id: `task-${nextId(s)}`,
      incident_id: incidentId,
      decision_id: decisionId,
      agency: t.agency,
      instruction_en: t.instruction?.en ?? t.instruction ?? "",
      instruction_mr: t.instruction?.mr ?? null,
      status: "assigned",
      assigned_at: nowIso(),
      acknowledged_at: null,
      acknowledged_by: null,
    }));
    s.tasks.push(...created);
    return created;
  });
  return result(rows, persisted);
}

export async function acknowledgeTask(taskId, officer) {
  const { result: row, persisted } = mutate((s) => {
    const t = s.tasks.find((x) => x.id === taskId);
    if (!t) throw new Error(`Task ${taskId} not found.`);
    t.status = "acknowledged";
    t.acknowledged_at = nowIso();
    t.acknowledged_by = officer?.id ?? null;
    return t;
  });
  return result(row, persisted);
}

export async function tasksFor(incidentId) {
  return result(getState().tasks.filter((t) => t.incident_id === incidentId), true);
}

/* ── Observations ─────────────────────────────────────────────────────── */
export async function recordObservation(obs) {
  const { result: row, persisted } = mutate((s) => {
    const rec = { id: nextId(s), ...obs, at: obs.at || nowIso() };
    s.observations.push(rec);
    return rec;
  });
  return result(row, persisted);
}

export async function observationsFor(incidentId) {
  return result(getState().observations.filter((o) => o.incident_id === incidentId), true);
}

/* Test seam. */
export { clearStore as __resetLocal } from "./store";
